'use client';
import React, { useEffect, useState } from 'react';
import type { DelayedQuote } from '@benzinga/quotes-manager';
import type { TrendingTopic } from '@benzinga/trending-topics-manager';
import { Search } from '@benzinga/search-ui';
import { useHydrate } from '@benzinga/hooks';
import styles from './QuoteBar.module.scss';
import { DefaultQuotesList } from './_mockMenu';
import { QuotesList } from './QuotesList';
import { StockSymbol, type Ticker } from '@benzinga/session';

interface QuoteBarProps {
  hideSearchBar?: boolean;
  initialTrendingTickers?: Ticker[];
  initialTrendingTopics?: TrendingTopic[];
  marketTickers?: string[];
  onInput?: () => string;
  primaryQuotesLabel?: string;
  quotes?: Record<StockSymbol, DelayedQuote> | null;
  recentTickers?: string[];
}

export const QuoteBar: React.FC<QuoteBarProps> = React.memo(
  ({
    hideSearchBar,
    initialTrendingTickers,
    initialTrendingTopics,
    marketTickers,
    primaryQuotesLabel = 'Markets',
    quotes,
    recentTickers,
  }) => {
    const [activeQuotes, setActiveQuotes] = useState('markets');
    const nextTickers = marketTickers ?? DefaultQuotesList;
    const [tickers, setTickers] = useState<string[] | undefined>(nextTickers);
    const [tickerQuotes, setTickerQuotes] = useState<Record<StockSymbol, DelayedQuote> | null | undefined>(quotes);
    const [listFaded, setListFaded] = useState(false);
    const displayedTickersKeyRef = React.useRef((nextTickers ?? []).join(','));

    const isHydrated = useHydrate(true, false);
    const showRecentToggle = isHydrated && (recentTickers?.length ?? 0) > 0;
    const nextTickersKey = (nextTickers ?? []).join(',');

    useEffect(() => {
      if (!showRecentToggle && activeQuotes === 'recent') {
        setActiveQuotes('markets');
      }
    }, [showRecentToggle, activeQuotes]);

    useEffect(() => {
      let fadeTimeout: number | undefined;

      if (showRecentToggle && activeQuotes === 'recent') {
        setTickers(recentTickers);
        setTickerQuotes(quotes);
        displayedTickersKeyRef.current = (recentTickers ?? []).join(',');
      } else if (
        displayedTickersKeyRef.current &&
        nextTickersKey &&
        displayedTickersKeyRef.current !== nextTickersKey
      ) {
        setListFaded(true);
        fadeTimeout = window.setTimeout(() => {
          setTickers(nextTickers);
          setTickerQuotes(quotes);
          displayedTickersKeyRef.current = nextTickersKey;
          setListFaded(false);
        }, 140);
      } else {
        setTickers(nextTickers);
        setTickerQuotes(quotes);
        displayedTickersKeyRef.current = nextTickersKey;
      }

      return () => {
        if (fadeTimeout !== undefined) {
          window.clearTimeout(fadeTimeout);
        }
      };
    }, [showRecentToggle, activeQuotes, nextTickersKey, quotes, recentTickers, nextTickers]);

    return (
      <div className={`${styles.quoteBarContainer} quote-bar-container`}>
        <div className="quote-bar-layout-width">
          <div className="quote-bar-wrapper">
            <QuotesSwitcher
              activeQuotes={activeQuotes}
              onClick={setActiveQuotes}
              primaryQuotesLabel={primaryQuotesLabel}
              showRecentToggle={showRecentToggle}
            />
            <div className={`quote-list-container ${listFaded ? styles.quoteListFaded : styles.quoteListReady}`}>
              <QuotesList quotes={tickerQuotes} tickers={tickers} />
            </div>
            {!hideSearchBar ? (
              <div className="search-wrapper">
                <Search
                  initialTrendingTickers={initialTrendingTickers}
                  initialTrendingTopics={initialTrendingTopics}
                  placeholder="Search Tickers, Companies or News..."
                  showBackdrop
                  showTrending
                />
              </div>
            ) : null}
          </div>
        </div>
      </div>
    );
  },
);

const QuotesSwitcher: React.FC<{
  activeQuotes: string;
  onClick: (value: string) => void;
  primaryQuotesLabel: string;
  showRecentToggle: boolean;
}> = ({ activeQuotes, onClick, primaryQuotesLabel, showRecentToggle }) => {
  const getButtonClasses = (isActive: boolean) =>
    `max-w-[73px] w-full border-l text-left text-xs leading-[18px] py-1 px-2.5 grow focus:outline-none ${
      isActive
        ? 'border-bzgray-100 bg-[linear-gradient(90deg,rgba(67,160,248,0.1)_0%,rgba(67,160,248,0)_100%)] text-white'
        : 'border-bzgrey-400 bg-[linear-gradient(90deg,rgba(129,139,151,0.1)_0%,rgba(129,139,151,0)_100%)] text-bzgrey-400'
    }`;

  return (
    <div className="quote-switcher-wrapper overflow-y-auto h-[52px] w-[70px] scrollbar-none flex-none [&::-webkit-scrollbar]:hidden">
      <div className="quote-switcher-container flex flex-col">
        <button
          aria-pressed={activeQuotes === 'markets'}
          className={getButtonClasses(activeQuotes === 'markets')}
          data-action="Quote Bar Markets Click"
          onClick={() => onClick('markets')}
        >
          {primaryQuotesLabel}
        </button>
        {showRecentToggle ? (
          <button
            aria-pressed={activeQuotes === 'recent'}
            className={getButtonClasses(activeQuotes === 'recent')}
            data-action="Quote Bar Recent Click"
            onClick={() => onClick('recent')}
          >
            Recent
          </button>
        ) : null}
      </div>
    </div>
  );
};
